import { useState, useEffect, SyntheticEvent } from "react";
import { Box, Grid, Card, CardContent, Typography, Button, TextField, useMediaQuery, CircularProgress, Divider } from "@mui/material";
import { Search as SearchIcon, Person as PersonIcon } from '@mui/icons-material';
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import Title from "../../components/Title";
import ModalClientHistory from "./modals/ModalClientHistory";
import { useDrawer } from "../../components/context/DrawerContext";
import { stickyListRegionWidth } from "../../components/layouts/StickyListToolbar";
import { useSelection } from "../../components/context/SelectionContext";
import CollectByOfficeForm from "../../components/filter/collectbyOffice";
import { Office } from "../../models/office";
import { Collect } from "../../models/collect";
import SimpleCustomTable from "../../components/table/SimpleCustomTable";
import { SimpleColumn, SimpleRow, SimpleColumnGroup, RowId } from "../../components/table/interface";
import HeaderResponsive from "../../components/layouts/HeaderResponsive";
import EmptyStateMessage from "../../components/layouts/EmptyStateMessage";
import PermissionButton from "../../components/common/PermissionButton";
import ClientQuoteModal from "../../components/common/ClientQuoteModal";
import { useResourcePermissions } from "../../hooks/useResourcePermissions";
import { isSuperAdminUser } from "../../utils/authScope";
import HistoryIcon from '@mui/icons-material/History';
import {
  iconActionColumnSizing,
  simpleTableIconButtonSx,
} from "../../components/table/iconActionColumnSizing";

// Tipos anidados en la respuesta de cliente (API)
interface ClientOfficeRef {
  id: number;
  name: string;
  phone: string;
  address: string;
  city: string;
  type_country: {
    id: number;
    name: string;
  };
}

interface ClientCollectRef {
  id: number;
  name: string;
  office: ClientOfficeRef;
}

interface Client {
  id: number;
  idnumber: string;
  fullname: string;
  backup_alert_active?: boolean;
  backups?: Array<{ id: number }>;
  quote: number;
  phone: string;
  cellphone: string;
  mainaddress: string;
  secondaddress: string;
  job: string;
  coclient_fullname: string;
  coclient_idnumber: string;
  coclient_address: string;
  coclient_phone: string;
  collect: ClientCollectRef;
}

interface ClientResponse {
  success: boolean;
  data: Client[];
  total: number;
}

interface ClientesHistProps {
  showAlert: (
    message: string,
    severity?: "error" | "info" | "success" | "warning"
  ) => void;
  showLoading: () => void;
  hideLoading: () => void;
}

// Componente principal
const ClientesHist: React.FC<ClientesHistProps> = ({
  showAlert,
  showLoading,
  hideLoading,
}) => {
  document.title = "Reporte Histórico Cliente";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [cedula, setCedula] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [selectedClientForQuote, setSelectedClientForQuote] = useState<Client | null>(null);
  const [authUser, setAuthUser] = useState<{ is_system?: number; username?: string } | null>(null);
  const { drawerOpen } = useDrawer();
  const { office_id, collect_id, setOffice_id, setCollect_id } = useSelection();
  const [offices, setOffices] = useState<Office[]>([]);
  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const { canRead, canUpdateQuote, canManageBackupAlert } = useResourcePermissions('/informes/historicocliente');
  const isSuperAdmin = isSuperAdminUser(authUser);

  useEffect(() => {
    const loadOffices = async () => {
      try {
        const [userResponse, officesResponse] = await Promise.all([
          axios.get("user"),
          axios.get("offices"),
        ]);
        setAuthUser(userResponse?.data?.data ?? null);
        const list = Array.isArray(officesResponse?.data?.data) ? officesResponse.data.data : [];
        setOffices(list);
      } catch (err) {
        console.error("Error cargando oficinas:", err);
      }
    };
    void loadOffices();
  }, []);

  useEffect(() => {
    if (offices.length === 0) return;
    setOffice_id((prev) =>
      prev && !offices.some((o) => String(o.id) === String(prev)) ? "" : prev
    );
  }, [offices, setOffice_id]);

  useEffect(() => {
    if (!office_id) {
      setCollectsOffice([]);
      return;
    }
    let cancelled = false;
    const loadCollects = async () => {
      try {
        const { data } = await axios.get(`collectsoffice/${office_id}`);
        const list = Array.isArray(data) ? data : [];
        if (cancelled) return;
        setCollectsOffice(list);
        setCollect_id((prev) =>
          prev && !list.some((c) => String(c.id) === String(prev)) ? "" : prev
        );
      } catch (err) {
        if (!cancelled) {
          console.error("Error cargando cobros:", err);
          setCollectsOffice([]);
        }
      }
    };
    void loadCollects();
    return () => {
      cancelled = true;
    };
  }, [office_id, setCollect_id]);

  useEffect(() => {
    setClients([]);
  }, [office_id, collect_id]);

  const performSearch = async () => {
    const trimmedSearch = cedula.trim();

    if (!trimmedSearch) {
      showAlert("Debe ingresar una cédula o nombre para buscar", "warning");
      return;
    }

    if (isSuperAdmin) {
      setLoading(true);
      try {
        const response = await axios.post<ClientResponse>("/clients/searchclients", {
          search: trimmedSearch,
        });

        if (response.data.success && response.data.data.length > 0) {
          setClients(response.data.data);
          showAlert(`Se encontraron ${response.data.data.length} clientes`, "success");
        } else {
          setClients([]);
          showAlert("No se encontraron clientes con esos criterios", "info");
        }
      } catch (error) {
        console.error("Error searching clients:", error);
        showAlert(
          "Error al buscar clientes. Inténtelo de nuevo más tarde.",
          "error"
        );
        setClients([]);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!String(office_id ?? "").trim() || !String(collect_id ?? "").trim()) {
      showAlert("Seleccione oficina y cobro antes de buscar", "warning");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post<ClientResponse>("/clients/searchclients", {
        search: trimmedSearch,
        office_id: Number(office_id),
        collect_id: Number(collect_id),
      });

      if (response.data.success && response.data.data.length > 0) {
        setClients(response.data.data);
        showAlert(`Se encontraron ${response.data.data.length} clientes`, "success");
      } else {
        setClients([]);
        showAlert("No se encontraron clientes con esos criterios", "info");
      }
    } catch (error) {
      console.error("Error searching clients:", error);
      showAlert(
        "Error al buscar clientes. Inténtelo de nuevo más tarde.",
        "error"
      );
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    await performSearch();
  };

  const baseColumns: SimpleColumn[] = [
    { field: 'idnumber', headerName: 'Cédula', flex: 0.8, minWidth: 120 },
    { field: 'fullname', headerName: 'Cliente', flex: 1.5, minWidth: 140, maxWidth: 180, wrapText: true },
    { field: 'phone', headerName: 'Teléfono', flex: 1, minWidth: 100, maxWidth: 100, },
    { field: 'cellphone', headerName: 'Celular', flex: 1, minWidth: 100, maxWidth: 110, },
    {
      field: 'quote',
      headerName: 'Cupo',
      flex: 0.8,
      minWidth: 90,
      maxWidth: 100,
      align: 'right',
      renderCell: ({ row }: { row: SimpleRow }) => Number(row.quote ?? 0),
    },
    { field: 'mainaddress', headerName: 'Dirección', flex: 1.5, minWidth: 140, maxWidth: 180, wrapText: true },
    {
      field: 'office',
      headerName: 'Oficina',
      flex: 1,
      minWidth: 80,
      maxWidth: 120,
      renderCell: ({ row }: { row: SimpleRow }) => row.collect?.office?.name ?? 'N/A',
    },
    {
      field: 'collect_name',
      headerName: 'Cobro',
      flex: 1,
      minWidth: 80,
      maxWidth: 120,
      wrapText: true,
    },
  ];

  const historyColumn: SimpleColumn = {
    field: 'opt_history',
    headerName: '',
    ...iconActionColumnSizing(isMobile),
    align: 'center',
    renderCell: ({ row }: { row: SimpleRow }) => (
      <PermissionButton
        allowed={canRead}
        deniedTooltip="Sin permiso para ver historial"
        tooltip="Historial"
        variant="contained"
        color="primary"
        sx={simpleTableIconButtonSx}
        onClick={() => handleOpenModal(row as Client)}
      >
        <HistoryIcon fontSize="small" />
      </PermissionButton>
    ),
  };

  const quoteColumn: SimpleColumn = {
    field: 'opt_quote',
    headerName: '',
    ...iconActionColumnSizing(isMobile),
    align: 'center',
    renderCell: ({ row }: { row: SimpleRow }) => (
      <PermissionButton
        allowed={canUpdateQuote}
        deniedTooltip="Sin permiso para modificar cupo"
        tooltip="Modificar cupo"
        variant="contained"
        color="info"
        sx={simpleTableIconButtonSx}
        onClick={() => {
          setSelectedClientForQuote(row as Client);
          setQuoteModalOpen(true);
        }}
      >
        <PersonIcon fontSize="small" />
      </PermissionButton>
    ),
  };

  const simpleColumns: SimpleColumn[] = [...baseColumns, historyColumn, quoteColumn];

  const mobileColumns: SimpleColumn[] = [
    simpleColumns.find((col) => col.field === "idnumber")!,
    simpleColumns.find((col) => col.field === "fullname")!,
    simpleColumns.find((col) => col.field === "quote")!,
    simpleColumns.find((col) => col.field === "collect_name")!,
    simpleColumns.find((col) => col.field === "office")!,
    historyColumn,
    quoteColumn,
  ];

  const handleOpenModal = (client: Client) => {
    setSelectedClient(client);
    setOpenModal(true);
  };

  // Preparar datos para la tabla
  const rows: SimpleRow[] = [...clients]
    .sort((a, b) => {
      const officeCompare = String(a.collect?.office?.name || "").localeCompare(
        String(b.collect?.office?.name || ""),
        "es",
        { sensitivity: "base" }
      );
      if (officeCompare !== 0) return officeCompare;

      const collectCompare = String(a.collect?.name || "").localeCompare(
        String(b.collect?.name || ""),
        "es",
        { sensitivity: "base" }
      );
      if (collectCompare !== 0) return collectCompare;

      return String(a.fullname || "").localeCompare(String(b.fullname || ""), "es", {
        sensitivity: "base",
      });
    })
    .map((client) => ({
      ...client,
      id: client.id as RowId,
      collect_name: client.collect?.name ?? "N/A",
    }));

  const displayColumns = isMobile ? mobileColumns : simpleColumns;
  const columnGroups: SimpleColumnGroup[] = [{ header: 'Opciones', fields: ['opt_history', 'opt_quote'] }];

  return (
    <HeaderResponsive sx={{ py: 0, px: 0.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Title>Histórico Cliente</Title>
        <Button
          type="submit"
          form="form-clientes-hist"
          variant="contained"
          startIcon={!isMobile && !loading ? <SearchIcon /> : undefined}
          disabled={loading}
        >
          {isMobile ? <SearchIcon /> : loading ? "Buscando…" : "Buscar"}
        </Button>
      </Box>

      <Card
        sx={{
          width: (t) => stickyListRegionWidth(t, drawerOpen), maxWidth: '100%', boxSizing: 'border-box', overflowX: 'auto',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <form id="form-clientes-hist" onSubmit={handleSubmit}>
          <CardContent
            sx={{
              width: (t) => stickyListRegionWidth(t, drawerOpen),
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflowX: 'auto',
              flexShrink: 0,
              pt: isSuperAdmin ? 1 : undefined,
              scrollbarColor: '#0768c9 #d9e1ea',
              '&::-webkit-scrollbar': {
                width: 12,
                height: 12,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#d9e1ea',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#0768c9',
                borderRadius: 999,
                border: '2px solid #d9e1ea',
              },
              '&::-webkit-scrollbar-corner': {
                backgroundColor: '#d9e1ea',
              },
            }}
          >
            {!isSuperAdmin ? (
              <CollectByOfficeForm offices={offices} collectsOffice={collectsOffice}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Cédula o Nombre"
                    value={cedula}
                    onChange={(event) => setCedula(event.target.value)}
                    disabled={loading}
                    InputLabelProps={{ shrink: true }}
                    placeholder="Ej: 71397900 o José"
                  />
                </Grid>
              </CollectByOfficeForm>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Cédula o Nombre"
                    value={cedula}
                    onChange={(event) => setCedula(event.target.value)}
                    disabled={loading}
                    InputLabelProps={{ shrink: true }}
                    helperText="Búsqueda global disponible por cédula o nombre."
                    placeholder="Ej: 71397900 o José"
                  />
                </Grid>
              </Grid>
            )}
            <Divider sx={{ my: 1 }} />
          </CardContent>
        </form>

        <CardContent
          sx={{
            width: (theme) => stickyListRegionWidth(theme, drawerOpen),
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'auto',
            pt: 0,
            pb: 4,
          }}
        >
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            {clients.length > 0
              ? `${clients.length} clientes encontrados`
              : isSuperAdmin
                ? "Ingrese una cédula para buscar en todo el sistema"
                : "Elija oficina y cobro, luego busque por cédula"}
          </Typography>

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={200}>
              <CircularProgress />
            </Box>
          ) : clients.length > 0 ? (
            <SimpleCustomTable
              rows={rows}
              columns={displayColumns}
              disableInternalScroll
              showBorders
              showCount
              exportCsv
              exportFileName="clientes-historico.csv"
              wrapCellContent
              columnGroups={columnGroups}
            />
          ) : (
            <EmptyStateMessage
              message={
                !isSuperAdmin && (!office_id || !collect_id)
                  ? "Seleccione oficina y cobro para consultar el histórico"
                  : cedula
                    ? isSuperAdmin
                      ? "No se encontraron clientes con esos criterios"
                      : "No se encontraron clientes en este cobro"
                    : "Ingrese cédula o nombre para buscar"
              }
              align="center"
            />
          )}
        </CardContent>
      </Card>
      <Box sx={{ height: { xs: 48, md: 32 } }} />

      <ModalClientHistory
        open={openModal}
        onClose={() => setOpenModal(false)}
        canManageBackupAlert={canManageBackupAlert}
        onBackupAlertChange={(clientId, active) => {
          setClients((current) =>
            current.map((client) =>
              client.id === clientId
                ? { ...client, backup_alert_active: active }
                : client
            )
          );
          setSelectedClient((current) =>
            current && current.id === clientId
              ? { ...current, backup_alert_active: active }
              : current
          );
        }}
        clientData={selectedClient ? {
          id: selectedClient.id,
          cedula: selectedClient.idnumber?.toString() || "",
          nombre: selectedClient.fullname || "",
          direccion: selectedClient.mainaddress || "",
          telefono: selectedClient.phone || "",
          oficio: selectedClient.job || "",
          calificacion: "",
          celular: selectedClient.cellphone || "",
          collectId:
            selectedClient.collect?.id ??
            (Number.isFinite(Number(collect_id)) && Number(collect_id) > 0
              ? Number(collect_id)
              : undefined),
          backup_alert_active: Boolean((selectedClient as any).backup_alert_active),
          backups: (selectedClient as any).backups ?? [],
        } : null}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
      <ClientQuoteModal
        open={quoteModalOpen}
        client={selectedClientForQuote}
        onClose={() => {
          setQuoteModalOpen(false);
          setSelectedClientForQuote(null);
        }}
        onSaved={(nextQuote) => {
          setClients((current) =>
            current.map((client) =>
              client.id === selectedClientForQuote?.id
                ? { ...client, quote: nextQuote }
                : client
            )
          );
        }}
        showAlert={showAlert}
      />
    </HeaderResponsive>
  );
};

export default ClientesHist;
